/**
 * Shared multi-page utilities used by both campaigns and tools.
 */
import type { MultiPageDef } from '../../types';

/** Generate a short unique ID for pages */
export function genPageId(): string {
  return 'pg_' + Math.random().toString(36).slice(2, 9);
}

/** Create a default first page */
export function createDefaultPage(name = 'עמוד 1'): MultiPageDef {
  return { id: genPageId(), name, order: 0 };
}

/** Sort pages by order */
export function sortedPages(pages: MultiPageDef[]): MultiPageDef[] {
  return [...pages].sort((a, b) => a.order - b.order);
}

/** Get the first page id, or undefined if no pages */
export function firstPageId(pages?: MultiPageDef[]): string | undefined {
  if (!pages || pages.length === 0) return undefined;
  return sortedPages(pages)[0].id;
}

/**
 * Resolve which page a block belongs to.
 * If blockPageId is set and exists in pages, use it.
 * Otherwise default to first page.
 */
export function resolveBlockPageId(
  blockPageId: string | undefined,
  pages: MultiPageDef[]
): string {
  if (blockPageId && pages.some(p => p.id === blockPageId)) return blockPageId;
  return sortedPages(pages)[0].id;
}

/** Check if multi-page mode is active */
export function isMultiPage(pages?: MultiPageDef[]): boolean {
  return Array.isArray(pages) && pages.length > 1;
}

/** Reorder pages after move */
export function reorderPages(pages: MultiPageDef[], fromIdx: number, toIdx: number): MultiPageDef[] {
  const sorted = sortedPages(pages);
  const [moved] = sorted.splice(fromIdx, 1);
  sorted.splice(toIdx, 0, moved);
  return sorted.map((p, i) => ({ ...p, order: i }));
}

/**
 * Check if it's safe to delete a page (must have at least 1 page remaining).
 */
export function canDeletePage(pages: MultiPageDef[]): boolean {
  return pages.length > 1;
}
